// Outlets.jsx
import { Link } from "react-router-dom";
import { Button, Input, message, Modal } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import SearchBar from "../SearchBar";
import TableMed from "../TableMed";
import {
  addStampCode,
  deleteOutlet,
  deleteStampCode,
  getOutlets,
} from "../../actions/acc_actions";
import { useRef, useState } from "react";
import MyModal from "../MyModal";

export default function Outlets({ businessUrn }) {
  const modalRef = useRef();
  const [stampCode, setStampCode] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState(null);

  // Fetch outlets
  const { isLoading, isFetching, refetch, data } = useQuery({
    queryKey: ["outlets", businessUrn],
    queryFn: () => getOutlets(businessUrn),
    enabled: !!businessUrn,
  });

  const handleAddStampCode = () => {
    if (!selectedOutlet) return;

    addStampCode(selectedOutlet.urn, businessUrn, { stampCode })
      .then(() => {
        refetch();
        setSelectedOutlet(null);
        modalRef.current?.hideModal();
        setStampCode("");
        message.success("Stamp Code Added Successfully.");
      })
      .catch((error) => {
        // Try to extract validation error message
        const apiMsg =
          error?.response?.data?.error?.errors?.[0]?.msg ||
          error?.response?.data?.message ||
          "Something went wrong!";

        message.error(apiMsg);
      });
  };

  const handleAddStamp = (outlet) => {
    setSelectedOutlet(outlet);
    modalRef.current?.showModal();
  };

  const handleDeleteStampCode = (outletUrn, stampCode) => {
    refetch();
    Modal.confirm({
      title: "Confirm Delete Stamp Code",
      content: `Are you sure you want to delete the stamp code "${stampCode}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        return deleteStampCode({ businessUrn, outletUrn, stampCode })
          .then(() => {
            refetch();
            setSelectedOutlet(null);
            modalRef.current.hideModal();
            message.success("Stamp code deleted successfully.");
          })
          .catch((error) => {
            message.error("Failed to delete stamp code.");
          });
      },
    });
  };

  // Handle Delete with confirmation
  const handleDelete = (outletUrn) => {
    Modal.confirm({
      title: "Confirm Delete",
      content:
        "Are you sure you want to delete this outlet? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        return deleteOutlet({ businessUrn, outletUrn })
          .then(() => {
            refetch();
            message.success("Outlet deleted successfully.");
          })
          .catch((error) => {
            message.error("Failed to delete outlet.");
          });
      },
    });
  };

  const columns = [
    {
      title: "URN",
      dataIndex: "urn",
      key: "urn",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Address",
      key: "address",
      render: (_, record) => {
        const addr = record.address || {};
        return (
          `${addr.house_number || ""} ${addr.street_name || ""}, ${
            addr.town || ""
          }, ${addr.postal_code || ""}`.trim() || "N/A"
        );
      },
    },
    {
      title: "Opening Times",
      key: "opening_times",
      render: (_, record) => record.config?.opening_times?.join(", ") || "N/A",
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: (text) => new Date(text).toLocaleString() || "N/A",
    },
    {
      title: "Business URN",
      dataIndex: "business_urn",
      key: "business_urn",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="form-row">
          <Button
            className="delete-btn"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.urn)}
            danger
          ></Button>
          <Button
            className="add-stamp-btn"
            icon={<PlusOutlined />}
            onClick={() => handleAddStamp(record)}
          >
            Add Stamp
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="features-tab">
      <div className="form-row">
        <Link to="create" className="btn primary">
          Create
        </Link>
        <SearchBar />
      </div>
      <TableMed
        loading={isLoading || isFetching}
        data={data?.data?.body || []}
        columns={columns}
      />
      <MyModal
        onClose={refetch}
        ref={modalRef}
        title="Stamp Code"
        message="Add Your Stamp Device Code To Connect To Your outlet"
        imageSrc="/assets/images/modal/stamp.png"
        additionalContent={
          <>
            <Input
              value={stampCode}
              onChange={(e) => setStampCode(e.target.value)}
              placeholder="Enter stamp code"
            />
            <Button
              style={{ width: "100%", marginBottom: "16px" }}
              type="primary"
              onClick={handleAddStampCode}
            >
              Add
            </Button>
            {selectedOutlet?.stamp_codes?.length > 0 && (
              <div className="list">
                <h6>Stamp Codes</h6>
                {selectedOutlet.stamp_codes.map((code) => (
                  <div className="row" key={code}>
                    <h5 className="stamp-code">{code}</h5>
                    <Button
                      type="default"
                      danger
                      onClick={() =>
                        handleDeleteStampCode(selectedOutlet.urn, code)
                      }
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        }
      />
    </div>
  );
}
